import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Loading from "../../../components/utils/Loading";
import {
  getWasteStatistic,
  getStatisticsCollections,
} from "../../../services/container";
import { getSettingsData } from "../../../services/settings";
import { useTranslation } from "react-i18next";
import LineAndBarChart from "../../../components/charts/LineAndBarChart";
import ValorizationRecommendationsSection from "../components/ValorizationRecommendationsSection";
import {
  buildValorizationMainIssues,
  buildValorizationRecommendationActions,
  roundPercent,
} from "../utils/buildValorizationRecommendations";

const DEFAULT_OBJECTIVE_PERCENT = 80;

const parseValorizationObjective = (settingsResponse) => {
  const raw = settingsResponse?.data?.settings;
  const settings = Array.isArray(raw) ? raw[0] : raw;
  const v = settings?.valorizationObjectivePercent;
  if (v === undefined || v === null || v === "")
    return DEFAULT_OBJECTIVE_PERCENT;
  const n = Number(v);
  if (Number.isNaN(n)) return DEFAULT_OBJECTIVE_PERCENT;
  return Math.min(100, Math.max(0, n));
};

const getRate = (selectedVolume, totalVolume) => {
  if (!totalVolume || totalVolume <= 0) return 0;
  return roundPercent((selectedVolume / totalVolume) * 100);
};

const StatisticsValorization = ({
  periodHistoryStart,
  periodHistoryStop,
  periodHistoryStartPrevious,
  periodHistoryStopPrevious,
  enablePreviousPeriod,
  keywordFilter,
  handleValidate,
  setHandleValidate,
  onOpenRecommendationsGuide,
}) => {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [objectivePercent, setObjectivePercent] = useState(
    DEFAULT_OBJECTIVE_PERCENT,
  );
  const [summary, setSummary] = useState({
    currentRate: 0,
    previousRate: 0,
    deltaRate: 0,
    hasComparablePreviousPeriod: false,
    objectiveGap: -DEFAULT_OBJECTIVE_PERCENT,
    totalVolume: 0,
  });
  const [rows, setRows] = useState([]);
  const [monthlyCompliantRate, setMonthlyCompliantRate] = useState([]);

  useEffect(() => {
    setHandleValidate(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!handleValidate) return;

    const keywordValue = keywordFilter?.value ?? [];
    const previousRequest =
      enablePreviousPeriod &&
      periodHistoryStartPrevious &&
      periodHistoryStopPrevious
        ? getWasteStatistic({
            periodStart: periodHistoryStartPrevious,
            periodStop: periodHistoryStopPrevious,
            keywordFilter: JSON.stringify(keywordValue),
          })
        : Promise.resolve({
            status: 200,
            data: {
              statisticsData: {
                totalWasteVolume: 0,
                totalVolumeSortingError: 0,
              },
            },
          });

    const previousCollectionsRequest =
      enablePreviousPeriod &&
      periodHistoryStartPrevious &&
      periodHistoryStopPrevious
        ? getStatisticsCollections({
            periodStart: periodHistoryStartPrevious,
            periodStop: periodHistoryStopPrevious,
            keywordFilter: JSON.stringify(keywordValue),
          }).catch(() => ({
            data: { dataResult: {}, totalVolumeMaterial: 0 },
          }))
        : Promise.resolve({ data: { dataResult: {}, totalVolumeMaterial: 0 } });

    setIsLoading(true);
    axios
      .all([
        getWasteStatistic({
          periodStart: periodHistoryStart,
          periodStop: periodHistoryStop,
          keywordFilter: JSON.stringify(keywordValue),
        }),
        previousRequest,
        getSettingsData().catch(() => ({ data: { settings: {} } })),
        getStatisticsCollections({
          periodStart: periodHistoryStart,
          periodStop: periodHistoryStop,
          keywordFilter: JSON.stringify(keywordValue),
        }).catch(() => ({ data: { dataResult: {}, totalVolumeMaterial: 0 } })),
        previousCollectionsRequest,
      ])
      .then(
        axios.spread(
          (
            currentResponse,
            previousResponse,
            settingsResponse,
            collectionsResponse,
            previousCollectionsResponse,
          ) => {
            const targetRate = parseValorizationObjective(settingsResponse);
            setObjectivePercent(targetRate);

            const currentData = currentResponse?.data?.statisticsData || {};
            const previousData = previousResponse?.data?.statisticsData || {};
            const collectionsDataResultCurrent =
              collectionsResponse?.data?.dataResult || {};
            const collectionsDataResultPrevious =
              previousCollectionsResponse?.data?.dataResult || {};
            const valorizationEligibleByFluxCurrent =
              currentData.valorizationEligibleByFlux || {};
            const valorizationEligibleByFluxPrevious =
              previousData.valorizationEligibleByFlux || {};

            const dataVolume = currentData.dataVolume || {};
            const fluxRows = Object.entries(dataVolume).map(
              ([label, values]) => {
                const totalVolume = values?.totalWasteVolume || 0;
                const sortingErrorVolume = values?.totalVolumeSortingError || 0;
                const sortingErrorRate = getRate(
                  sortingErrorVolume,
                  totalVolume,
                );
                return {
                  label,
                  totalVolume,
                  sortingErrorVolume,
                  sortingErrorRate,
                  isValorizationEligible: Boolean(
                    valorizationEligibleByFluxCurrent?.[label],
                  ),
                };
              },
            );

            const fluxRowsScaledForDisplay = fluxRows.map((row) => {
              const collectionsVolume =
                collectionsDataResultCurrent?.[row.label]
                  ?.totalWasteVolumeMaterial ?? 0;

              // Scale only when we have a comparable rotations volume for this flux label.
              const scaleFactor =
                row.totalVolume > 0 && collectionsVolume > 0
                  ? collectionsVolume / row.totalVolume
                  : 1;

              return {
                ...row,
                totalVolume: row.totalVolume * scaleFactor,
                sortingErrorVolume: row.sortingErrorVolume * scaleFactor,
                volumeWithoutSortingErrors: Math.max(
                  0,
                  (row.totalVolume - row.sortingErrorVolume) * scaleFactor,
                ),
              };
            });

            const currentTotal = fluxRowsScaledForDisplay.reduce(
              (acc, row) => acc + row.volumeWithoutSortingErrors,
              0,
            );
            const currentValorisableVolume = fluxRowsScaledForDisplay.reduce(
              (acc, row) =>
                acc +
                (row.isValorizationEligible
                  ? row.volumeWithoutSortingErrors
                  : 0),
              0,
            );
            const currentRate = getRate(currentValorisableVolume, currentTotal);

            const previousDataVolume = previousData.dataVolume || {};
            const previousFluxRows = Object.entries(previousDataVolume).map(
              ([label, values]) => {
                const totalVolume = values?.totalWasteVolume || 0;
                const sortingErrorVolume = values?.totalVolumeSortingError || 0;
                return {
                  label,
                  totalVolume,
                  sortingErrorVolume,
                  isValorizationEligible: Boolean(
                    valorizationEligibleByFluxPrevious?.[label],
                  ),
                };
              },
            );

            const previousFluxRowsScaledForDisplay = previousFluxRows.map(
              (row) => {
                const collectionsVolume =
                  collectionsDataResultPrevious?.[row.label]
                    ?.totalWasteVolumeMaterial ?? 0;
                const scaleFactor =
                  row.totalVolume > 0 && collectionsVolume > 0
                    ? collectionsVolume / row.totalVolume
                    : 1;

                return {
                  ...row,
                  totalVolume: row.totalVolume * scaleFactor,
                  sortingErrorVolume: row.sortingErrorVolume * scaleFactor,
                  volumeWithoutSortingErrors: Math.max(
                    0,
                    (row.totalVolume - row.sortingErrorVolume) * scaleFactor,
                  ),
                };
              },
            );

            const previousTotal = previousFluxRowsScaledForDisplay.reduce(
              (acc, row) => acc + row.volumeWithoutSortingErrors,
              0,
            );
            const previousValorisableVolume =
              previousFluxRowsScaledForDisplay.reduce(
                (acc, row) =>
                  acc +
                  (row.isValorizationEligible
                    ? row.volumeWithoutSortingErrors
                    : 0),
                0,
              );
            const previousRate = getRate(
              previousValorisableVolume,
              previousTotal,
            );

            const totalVolumeWithoutErrorsSafe =
              fluxRowsScaledForDisplay.reduce(
                (acc, row) => acc + row.volumeWithoutSortingErrors,
                0,
              );
            const hasComparablePreviousPeriod =
              enablePreviousPeriod && previousTotal > 0;
            const deltaRate = hasComparablePreviousPeriod
              ? roundPercent(currentRate - previousRate)
              : 0;

            const withShare = fluxRowsScaledForDisplay
              .map((row) => {
                const share = totalVolumeWithoutErrorsSafe
                  ? roundPercent(
                      (row.volumeWithoutSortingErrors /
                        totalVolumeWithoutErrorsSafe) *
                        100,
                    )
                  : 0;
                return {
                  ...row,
                  share,
                  valorizedShare: row.isValorizationEligible ? share : 0,
                  statusLabel: row.isValorizationEligible ? t("Yes") : t("No"),
                };
              })
              .sort((a, b) => b.totalVolume - a.totalVolume);

            const monthlyVolumeStatsByFlux =
              currentData.monthlyVolumeStatsByFlux || [];

            const scaleFactorByFlux = fluxRows.reduce((acc, row) => {
              const rotationsVolume =
                collectionsDataResultCurrent?.[row.label]
                  ?.totalWasteVolumeMaterial ?? 0;
              const scaleFactor =
                row.totalVolume > 0 && rotationsVolume > 0
                  ? rotationsVolume / row.totalVolume
                  : 1;
              acc[row.label] = scaleFactor;
              return acc;
            }, {});

            const monthlyTotals = {};
            monthlyVolumeStatsByFlux.forEach((item) => {
              const time = item.time;
              const flux = item.flux;
              if (!time || !flux) return;

              const totalWasteVolume = item?.totalWasteVolume || 0;
              const scaleFactor = scaleFactorByFlux[flux] ?? 1;
              const isValorizationEligible = Boolean(
                valorizationEligibleByFluxCurrent?.[flux],
              );
              const valorisableVolume = isValorizationEligible
                ? totalWasteVolume
                : 0;

              if (!monthlyTotals[time]) {
                monthlyTotals[time] = { total: 0, valorisable: 0 };
              }

              monthlyTotals[time].total += totalWasteVolume * scaleFactor;
              monthlyTotals[time].valorisable +=
                valorisableVolume * scaleFactor;
            });

            const monthlyTrend = Object.keys(monthlyTotals)
              .sort()
              .map((time) => {
                const tVals = monthlyTotals[time];
                return {
                  x: time,
                  y: getRate(tVals.valorisable, tVals.total),
                };
              });

            setSummary({
              currentRate,
              previousRate,
              deltaRate,
              hasComparablePreviousPeriod,
              objectiveGap: roundPercent(currentRate - targetRate),
              totalVolume: currentTotal,
            });
            setRows(withShare);
            setMonthlyCompliantRate(monthlyTrend);
          },
        ),
      )
      .catch(() => {
        setSummary({
          currentRate: 0,
          previousRate: 0,
          deltaRate: 0,
          hasComparablePreviousPeriod: false,
          objectiveGap: -DEFAULT_OBJECTIVE_PERCENT,
          totalVolume: 0,
        });
        setRows([]);
        setMonthlyCompliantRate([]);
      })
      .finally(() => {
        setIsLoading(false);
        setHandleValidate(false);
      });
  }, [
    enablePreviousPeriod,
    handleValidate,
    keywordFilter,
    periodHistoryStart,
    periodHistoryStartPrevious,
    periodHistoryStop,
    periodHistoryStopPrevious,
    setHandleValidate,
    t,
  ]);

  const mainIssue = useMemo(() => {
    const { messages, allNonValorisable } = buildValorizationMainIssues(
      rows,
      t,
    );
    return { messages, allNonValorisable };
  }, [rows, t]);

  const recommendations = useMemo(
    () =>
      buildValorizationRecommendationActions({
        rows,
        currentRate: summary.currentRate,
        objectivePercent,
        t,
        allNonValorisable: mainIssue.allNonValorisable,
      }),
    [
      mainIssue.allNonValorisable,
      objectivePercent,
      rows,
      summary.currentRate,
      t,
    ],
  );

  const formatVolume = (value) =>
    `${Math.round(value).toLocaleString(i18n.language)} m³`;

  if (isLoading) {
    return (
      <div className="w-full">
        <Loading height={"45vh"} />
      </div>
    );
  }

  const kpiCards = [
    {
      label: t("Valorization rate"),
      value: `${summary.currentRate}%`,
      hint: t("Valorization rate help short"),
    },
    {
      label: t("Target"),
      value: `${objectivePercent}%`,
      hint: null,
    },
    {
      label: t("Gap"),
      value: `${summary.objectiveGap > 0 ? "+" : ""}${summary.objectiveGap}%`,
      hint: null,
    },
    {
      label: t("Variation"),
      value: summary.hasComparablePreviousPeriod
        ? `${summary.deltaRate > 0 ? "+" : ""}${summary.deltaRate}%`
        : "–",
      hint: summary.hasComparablePreviousPeriod
        ? t("Variation help short")
        : t("No comparable previous period"),
    },
  ];

  return (
    <div className="text-blue w-full flex flex-col gap-6">
      <section className="grid grid-cols-1 min-lg:grid-cols-4 gap-6">
        {kpiCards.map((card) => (
          <article key={card.label} className="statistics-card">
            <p className="text-sm text-[#5e5e5e] mb-1">{card.label}</p>
            <p className="text-xl font-bold text-[#000000]">{card.value}</p>
            {card.hint ? (
              <p className="text-xs text-[#5e5e5e] mt-1">{card.hint}</p>
            ) : null}
          </article>
        ))}
      </section>

      <section className="grid grid-cols-1 min-lg:grid-cols-3 gap-6">
        <article className="statistics-card min-lg:col-span-2">
          <div className="dashboard-chart-heading">
            <h5 className="dashboard-chart-title">{t("Details by flux")}</h5>
            <p className="dashboard-chart-subtitle">
              {t("clarity.table.formula")}
            </p>
          </div>
          <div className="flex flex-col overflow-x-auto">
            <div className="flex items-center gap-2 border-b border-[#e0e0e0] pb-2 text-[#000000] min-w-[720px]">
              <span className="flex-1 font-semibold">{t("Flux")}</span>
              <span className="flex-1 font-semibold">
                {t("Valorisable Flux")}
              </span>
              <span className="flex-1 text-right font-semibold">
                {t("Total volume")}
              </span>
              <span className="flex-1 text-right font-semibold">
                {t("Sorting errors")}
              </span>
              <span className="flex-1 text-right font-semibold">
                {t("Valorisable volume")}
              </span>
              <span className="flex-1 text-right font-semibold">
                {t("Contribution to valorisable volume")}
              </span>
            </div>
            {rows.map((row) => (
              <div
                key={row.label}
                className="flex items-center gap-2 py-3 border-b border-[#F0F0F0] text-[#5e5e5e] min-w-[720px]"
              >
                <span className="flex-1 min-w-0 truncate">{row.label}</span>
                <span className="flex-1">
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      row.isValorizationEligible
                        ? "bg-[#E9FBFB] text-[#008685]"
                        : "bg-[#FFE2E5] text-[#B42318]"
                    }`}
                  >
                    {row.statusLabel}
                  </span>
                </span>
                <span className="flex-1 text-right">
                  {formatVolume(row.totalVolume)}
                </span>
                <span className="flex-1 text-right">
                  {row.sortingErrorRate}%
                </span>
                <span className="flex-1 text-right">
                  {row.isValorizationEligible
                    ? formatVolume(row.volumeWithoutSortingErrors)
                    : "–"}
                </span>
                <span className="flex-1 text-right">
                  {row.isValorizationEligible ? `${row.share}%` : "–"}
                </span>
              </div>
            ))}
            {!rows.length ? (
              <p className="py-6 text-center text-[#5e5e5e]">
                {t("No valorization data for this period")}
              </p>
            ) : null}
          </div>
        </article>

        <ValorizationRecommendationsSection
          mainIssueMessages={mainIssue.messages}
          recommendations={recommendations}
          t={t}
          onOpenRecommendationsGuide={onOpenRecommendationsGuide}
        />
      </section>

      <section className="statistics-card">
        <div className="dashboard-chart-heading">
          <h5 className="dashboard-chart-title">
            {t("Monthly valorisable flux trend")}
          </h5>
        </div>
        <div className="h-[300px]">
          {monthlyCompliantRate.length > 0 ? (
            <LineAndBarChart
              dataChart={monthlyCompliantRate}
              type="line"
              labelsChart={monthlyCompliantRate.map((point) => point.x)}
              xAxisType="time"
              unitValueChart="%"
              maxYAxis={100}
              stepYAxis={10}
              messageLabelTooltip={t("Valorization rate")}
              xUnit="month"
            />
          ) : (
            <div className="h-full flex items-center justify-center text-[#5e5e5e]">
              {t("No monthly trend data")}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default StatisticsValorization;
